import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { apiError } from '@/api/admin';

interface Props {
  title: string;
  /** What will happen and what cannot be undone — a sentence or two. */
  message: React.ReactNode;
  confirmLabel: string;
  /** Colour of the confirming button; destructive actions use 'error'. */
  color?: 'primary' | 'error' | 'warning';
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}

/**
 * A confirmation the application owns: the consequence spelled out, the confirming button named
 * after the action, the dialog staying open with the error if the action fails. Replaces the
 * browser's `confirm()`, which cannot do any of that.
 */
export default function ConfirmDialog({ title, message, confirmLabel, color = 'primary', onConfirm, onClose }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true); setError(null);
    try { await onConfirm(); onClose(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color={color} onClick={confirm} disabled={busy} autoFocus>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}
